import React, {useEffect, useState} from 'react'
import {Field, ErrorMessage, Formik, Form} from 'formik'
import {KTSVG} from '../../../../_metronic/helpers'
import useUtils from '../useUtils'
import * as Yup from 'yup'
import AssignConfirmation from '../../../modules/alert/AssignConfirmation'
import {showAlert} from '../../../redux/reducers/alertSlice'
import {useDispatch} from 'react-redux'

function Step3() {
  const {modifySettings, fetchSettings} = useUtils()
  const [settings, setSettings] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const dispatch = useDispatch()

  const getSettings = async () => {
    try {
      setIsLoading(true)
      const data = await fetchSettings()
      setSettings(data)
    } catch (error) {
      console.error('Error fetching settings:', error?.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    async function loadSettings() {
      const data = await getSettings()
      if (data && !data.id) {
        console.error('Settings data missing ID:', data)
        dispatch(
          showAlert({
            message: 'Could not load settings properly. Please try again.',
            variant: 'warning',
            timeout: 5000,
          })
        )
      }
    }

    loadSettings()
  }, [])

  const currentSchema = Yup.object({
    mailHost: Yup.string().required('Mail Host is required').label('Mail Host'),
    mailPort: Yup.number().required('Mail Port is required').label('Mail Port'),
    mailUser: Yup.string().required('Mail User is required').label('Mail User'),
    mailPassword: Yup.string().required('Mail Password is required').label('Mail Password'),
    senderEmail: Yup.string()
      .email('Please enter a valid email address')
      .required('Sender Email is required')
      .label('Sender Email'),
  })

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true)
      // Trim spaces from all string fields
      const trimmedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) =>
          typeof value === 'string' ? [key, value.replace(/\s/g, '')] : [key, value]
        )
      )
      const {id, ...rest} = trimmedValues

      // Check if ID is valid
      if (!id) {
        dispatch(
          showAlert({
            message: 'Settings ID is missing. Please refresh the page and try again.',
            variant: 'danger',
            timeout: 5000,
          })
        )
        return
      }

      let response = await modifySettings(id, rest)
      if (response) {
        const latestSettings = await fetchSettings()
        setSettings({...latestSettings})
        setFormKey((prev) => prev + 1) // remount formik
      }
    } catch (error) {
      console.error('Error updating settings:', error)
      dispatch(
        showAlert({
          message: error?.response?.data?.message || 'Failed to update mail settings',
          variant: 'danger',
          timeout: 5000,
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='d-flex flex-column align-items-start mx-auto w-100 pt-15 pb-10'>
      {isLoading ? (
        <div className='d-flex w-100 justify-content-center'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : (
        <Formik
          key={formKey}
          enableReinitialize
          initialValues={{
            id: settings?.id || '',
            mailHost: settings?.mailHost || '',
            mailPort: settings?.mailPort || '',
            mailUser: settings?.mailUser || '',
            mailPassword: settings?.mailPassword || '',
            senderEmail: settings?.senderEmail || '',
          }}
          validationSchema={currentSchema}
          onSubmit={handleSubmit}
        >
          {({values, setFieldValue}) => (
            <Form id='kt_mail_settings_form'>
              <div className='w-100'>
                <div className='row mb-2' data-kt-buttons='true'>
                  <div className='mb-10 fv-row col-lg-6 ms-auto me-auto'>
                    <label className='col-form-label fw-bold required fs-6'>Mail Host</label>
                    <Field
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      name='mailHost'
                      onChange={(e) => setFieldValue('mailHost', e.target.value)}
                    />
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='mailHost' />
                    </div>
                  </div>

                  <div className='mb-10 fv-row col-lg-6 ms-auto me-auto'>
                    <label className='col-form-label fw-bold required fs-6'>Mail Port</label>
                    <Field
                      type='number'
                      className='form-control form-control-lg form-control-solid'
                      name='mailPort'
                      onChange={(e) => setFieldValue('mailPort', Number(e.target.value))}
                    />
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='mailPort' />
                    </div>
                  </div>

                  <div className='mb-10 fv-row col-lg-6 ms-auto me-auto'>
                    <label className='col-form-label fw-bold required fs-6'>Mail User</label>
                    <Field
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      name='mailUser'
                      onChange={(e) => setFieldValue('mailUser', e.target.value)}
                    />
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='mailUser' />
                    </div>
                  </div>

                  <div className='mb-10 fv-row col-lg-6 ms-auto me-auto'>
                    <label className='col-form-label fw-bold required fs-6'>Mail Password</label>
                    <div className='position-relative'>
                      <Field
                        type={showPassword ? 'text' : 'password'}
                        className='form-control form-control-lg form-control-solid'
                        name='mailPassword'
                        placeholder='Enter SMTP app password'
                        onChange={(e) => setFieldValue('mailPassword', e.target.value)}
                      />
                      <span
                        className='btn btn-sm btn-icon position-absolute translate-middle-y top-50 end-0 me-3'
                        onClick={() => setShowPassword(!showPassword)}
                        style={{cursor: 'pointer'}}
                      >
                        <KTSVG
                          path={
                            showPassword
                              ? '/media/icons/duotune/general/gen037.svg'
                              : '/media/icons/duotune/general/gen036.svg'
                          }
                          className='svg-icon svg-icon-2'
                        />
                      </span>
                    </div>
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='mailPassword' />
                    </div>
                  </div>

                  <div className='mb-10 fv-row col-lg-6 ms-auto me-auto'>
                    <label className='col-form-label fw-bold required fs-6'>Sender Email</label>
                    <Field
                      type='email'
                      className='form-control form-control-lg form-control-solid'
                      name='senderEmail'
                      placeholder='Enter sender email address'
                      onChange={(e) => setFieldValue('senderEmail', e.target.value)}
                    />
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='senderEmail' />
                    </div>
                  </div>
                </div>
              </div>

              <div className='d-flex flex-center pt-7'>
                <div>
                  <button
                    type='button'
                    onClick={() => {
                      setIsOpen(true)
                    }}
                    className='btn btn-lg btn-primary me-3'
                  >
                    <span className='indicator-label'>Save</span>
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
      <AssignConfirmation
        isVisible={isOpen}
        message='Are you sure you want to update the Mail Settings?'
        onConfirm={() => {
          setIsOpen(false)
          setTimeout(() => {
            document.querySelector('form#kt_mail_settings_form')?.requestSubmit()
          }, 0)
        }}
        onCancel={() => setIsOpen(false)}
      />
    </div>
  )
}

export default Step3
